import React, { useCallback, useEffect, useState } from 'react';
import { Users, LifeBuoy, AlertTriangle, UsersRound, BellRing, Phone, Mail, RefreshCw, Loader2, Check, StickyNote, Clock, Star, Car, ClipboardCheck, UserSearch, FileCheck2, CalendarPlus, KanbanSquare, List as ListIcon } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useChartColors } from './RevenueWidget';
import { toast } from 'sonner';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Skeleton } from '../../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { easerGet, easerPut, easerPost, easerDelete, errorMessage } from '../../../lib/easerApi';
import { StatusBadge, LEAD_STATUS, TICKET_STATE, EmptyLine, fmtDateTime, fmtDay, fmtDayYear, fmtHours, fmtEur, telHref } from './crmShared';
import { StarRating, fmtAvg } from '../qualite/qcShared';
import { tagStyle } from '../../../lib/jobTags';

export const SUIVI_VIEWS = [
  { id: 'rappels', label: 'Rappels', icon: BellRing },
  { id: 'prospection', label: 'Prospection', icon: UserSearch },
  { id: 'prospects', label: 'Prospects', icon: Users },
  { id: 'reclamations', label: 'Réclamations', icon: LifeBuoy },
  { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { id: 'equipe', label: 'Équipe', icon: UsersRound },
];

/** Rubrique « Suivi » du CRM : rappels, prospects du site, réclamations clients, incidents terrain, équipe. */
export function CrmSuivi({ view, onChangeView, onOpenClient, onUnauthorized }) {
  const current = SUIVI_VIEWS.some((v) => v.id === view) ? view : 'rappels';
  return (
    <div className="space-y-3" data-testid="crm-suivi">
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" aria-label="Suivi">
        {SUIVI_VIEWS.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={current === v.id} onClick={() => onChangeView(v.id)} data-testid={`crm-suivi-${v.id}`}
            className={`h-10 px-3 rounded-full border text-xs font-semibold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${current === v.id ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border text-muted-foreground hover:bg-secondary'}`}>
            <v.icon className="h-3.5 w-3.5" /> {v.label}
          </button>
        ))}
      </div>
      {current === 'rappels' && <Reminders onOpenClient={onOpenClient} onUnauthorized={onUnauthorized} />}
      {current === 'prospection' && <Prospection onOpenClient={onOpenClient} onUnauthorized={onUnauthorized} />}
      {current === 'prospects' && <Leads onUnauthorized={onUnauthorized} />}
      {current === 'reclamations' && <Tickets onOpenClient={onOpenClient} onUnauthorized={onUnauthorized} />}
      {current === 'incidents' && <Incidents onUnauthorized={onUnauthorized} />}
      {current === 'equipe' && <Team onUnauthorized={onUnauthorized} />}
    </div>
  );
}

function useLoader(fn, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setError('');
    try { setData(await fn()); } catch (err) { setError(errorMessage(err, 'Chargement impossible')); setData({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, error, reload: () => { setData(null); load(); }, setData };
}

function Loading() { return <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>; }
function ErrorCard({ error, onRetry }) { return <Card className="p-4 border-destructive/30" role="alert"><p className="text-sm">{error}</p><Button variant="outline" className="mt-2 h-10" onClick={onRetry}>Réessayer</Button></Card>; }

// ---------------------------------------------------------------- rappels

function Reminders({ onOpenClient, onUnauthorized }) {
  const [includeDone, setIncludeDone] = useState(false);
  const { data, error, reload, setData } = useLoader(async () => { try { return (await easerGet('/api/easer/crm/reminders', { include_done: includeDone })).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, [includeDone]);
  const [busy, setBusy] = useState(null);
  const done = async (n) => {
    setBusy(n.id);
    try { const res = await easerPut(`/api/easer/crm/notes/${n.id}`, { done: !n.done }); setData((d) => ({ ...d, items: (d.items || []).map((x) => (x.id === n.id ? res.data.note : x)).filter((x) => includeDone || !x.done) })); toast.success(n.done ? 'Rappel rouvert' : 'Rappel traité'); }
    catch (err) { toast.error(errorMessage(err, 'Mise à jour impossible')); } finally { setBusy(null); }
  };
  if (!data) return <Loading />;
  if (error) return <ErrorCard error={error} onRetry={reload} />;
  const items = data.items || [];
  const groups = [['En retard', items.filter((i) => i.overdue)], ["Aujourd'hui", items.filter((i) => i.due_today)], ['À venir', items.filter((i) => !i.overdue && !i.due_today && !i.done)], ['Traités', items.filter((i) => i.done)]];
  return (
    <div className="space-y-3" data-testid="crm-reminders">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{data.overdue} en retard · {data.today} aujourd'hui</p>
        <button type="button" onClick={() => setIncludeDone((v) => !v)} className="text-xs text-[hsl(var(--primary))] font-medium h-9 px-2" data-testid="crm-reminders-toggle-done">{includeDone ? 'Masquer les traités' : 'Voir les traités'}</button>
      </div>
      {items.length === 0 && <EmptyLine testId="crm-reminders-empty">Aucun rappel programmé. Ajoutez-en depuis la fiche d'un client (rubrique Suivi).</EmptyLine>}
      {groups.filter(([, list]) => list.length).map(([title, list]) => (
        <div key={title}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{title}</p>
          <ul className="space-y-2">
            {list.map((n) => (
              <li key={n.id} className={`rounded-xl border p-3 flex items-start gap-2 ${n.overdue ? 'border-destructive/40 bg-destructive/5' : n.due_today ? 'border-[hsl(var(--brand-apricot))]/60 bg-[hsl(var(--brand-apricot))]/10' : 'border-border'} ${n.done ? 'opacity-60' : ''}`} data-testid={`crm-reminder-${n.id}`}>
                <button type="button" onClick={() => done(n)} disabled={busy === n.id} aria-label={n.done ? 'Rouvrir' : 'Marquer comme fait'} className={`h-8 w-8 mt-0.5 rounded-full border flex items-center justify-center flex-shrink-0 ${n.done ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white' : 'border-border hover:bg-secondary'}`} data-testid={`crm-reminder-done-${n.id}`}>{busy === n.id ? <Loader2 className="h-4 w-4 animate-spin" /> : n.done ? <Check className="h-4 w-4" /> : null}</button>
                <button type="button" onClick={() => onOpenClient(n.socid, n.client_name)} className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium truncate">{n.client_name}</p>
                  {n.text && <p className={`text-sm text-muted-foreground whitespace-pre-line ${n.done ? 'line-through' : ''}`}>{n.text}</p>}
                  <p className="text-xs text-muted-foreground mt-0.5">{fmtDayYear(n.remind_at)} · {n.author_name}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- prospection : clients à relancer + devis acceptés à programmer

function Prospection({ onOpenClient, onUnauthorized }) {
  const [includeSnoozed, setIncludeSnoozed] = useState(false);
  const prospects = useLoader(async () => { try { return (await easerGet('/api/easer/crm/prospection', { include_snoozed: includeSnoozed })).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, [includeSnoozed]);
  const signed = useLoader(async () => { try { return (await easerGet('/api/easer/crm/devis-signes')).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, []);
  const [busy, setBusy] = useState(null);

  const snooze = async (p, days) => {
    setBusy(p.socid);
    try {
      if (p.snoozed_until) { await easerDelete(`/api/easer/crm/prospection/${p.socid}/snooze`); toast.success('Client remis dans la liste'); }
      else { await easerPost(`/api/easer/crm/prospection/${p.socid}/snooze`, { snooze_days: days }); toast.success(`Mis de côté ${days} jours`); }
      prospects.reload();
    } catch (err) { toast.error(errorMessage(err, 'Action impossible')); } finally { setBusy(null); }
  };

  const sq = signed.data;
  const pr = prospects.data;
  return (
    <div className="space-y-4" data-testid="crm-prospection">
      <section>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><FileCheck2 className="h-4 w-4 text-[hsl(var(--primary))]" /> Devis acceptés à programmer{sq?.count ? ` (${sq.count})` : ''}</h3>
          {sq?.count > 0 && <span className="text-xs text-muted-foreground">{fmtEur(sq.total_ttc)} TTC</span>}
        </div>
        {!sq && !signed.error && <Loading />}
        {signed.error && <ErrorCard error={signed.error} onRetry={signed.reload} />}
        {sq && (sq.items || []).length === 0 && <EmptyLine testId="crm-signed-empty">Tous les devis signés ont une intervention planifiée.</EmptyLine>}
        {sq?.items?.length > 0 && (
          <ul className="space-y-2" data-testid="crm-signed-list">
            {sq.items.map((d) => (
              <li key={d.propal_id}>
                <Card className="p-3 flex items-center gap-2" data-testid={`crm-signed-item-${d.propal_id}`}>
                  <span className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${d.days_since > 14 ? 'bg-destructive/10 text-destructive' : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}><CalendarPlus className="h-4 w-4" /></span>
                  <button type="button" onClick={() => onOpenClient(d.socid, d.client_name)} className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-medium truncate">{d.client_name} {d.is_new && <Badge className="ml-1 bg-destructive text-white border-0 text-[10px]">nouveau</Badge>}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.ref} · {fmtEur(d.total_ttc)} TTC · signé le {fmtDay(d.signed_at)} · il y a {d.days_since} j{d.town ? ` · ${d.town}` : ''}</p>
                  </button>
                  {d.phone && <a href={telHref(d.phone)} className="h-10 w-10 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0" aria-label={`Appeler ${d.client_name}`}><Phone className="h-4 w-4" /></a>}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><UserSearch className="h-4 w-4 text-[hsl(var(--primary))]" /> Clients à relancer{pr?.items ? ` (${pr.items.length})` : ''}</h3>
          <button type="button" onClick={() => setIncludeSnoozed((v) => !v)} className="text-xs text-[hsl(var(--primary))] font-medium h-9 px-2" data-testid="crm-prospection-toggle-snoozed">{includeSnoozed ? 'Masquer les mis de côté' : `Voir les mis de côté${pr?.snoozed ? ` (${pr.snoozed})` : ''}`}</button>
        </div>
        <p className="text-xs text-muted-foreground mb-2">Clients facturés en {pr?.year || new Date().getFullYear() - 1} sans aucune intervention prévue du {pr?.window ? `${fmtDay(pr.window.start)} au ${fmtDay(pr.window.end)}` : 'mois en cours au mois prochain'} : une prise de contact s'impose.</p>
        {!pr && !prospects.error && <Loading />}
        {prospects.error && <ErrorCard error={prospects.error} onRetry={prospects.reload} />}
        {pr && (pr.items || []).length === 0 && <EmptyLine testId="crm-prospection-empty">Aucun client à relancer : tous les clients de l'an dernier ont une prestation prévue.</EmptyLine>}
        {pr?.items?.length > 0 && (
          <ul className="space-y-2" data-testid="crm-prospection-list">
            {pr.items.map((p) => (
              <li key={p.socid}>
                <Card className={`p-3 space-y-2 ${p.snoozed_until ? 'opacity-70' : ''}`} data-testid={`crm-prospect-${p.socid}`}>
                  <div className="flex items-start gap-2">
                    <button type="button" onClick={() => onOpenClient(p.socid, p.name)} className="min-w-0 flex-1 text-left">
                      <p className="font-medium leading-snug truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.town}{p.town ? ' · ' : ''}{fmtEur(p.last_year_ttc)} TTC en {pr.year} ({p.last_year_count} facture{p.last_year_count > 1 ? 's' : ''}){p.this_year_count ? ` · ${fmtEur(p.this_year_ttc)} cette année` : ' · rien cette année'}</p>
                      <p className="text-xs text-muted-foreground">Dernière facture {fmtDayYear(p.last_invoice)}{p.months_inactive ? ` · inactif depuis ${p.months_inactive} mois` : ''}{p.notes ? ` · ${p.notes} note(s)` : ''}</p>
                      {p.snoozed_until && <p className="text-xs text-[hsl(var(--brand-ink))]">Mis de côté jusqu'au {fmtDayYear(p.snoozed_until)} par {p.snoozed_by}</p>}
                    </button>
                    <Badge className={`border text-[10px] whitespace-nowrap ${p.months_inactive >= 6 ? 'bg-destructive/10 text-destructive border-destructive/30' : 'bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60'}`}>{p.months_inactive ? `${p.months_inactive} mois` : 'à relancer'}</Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.phone && <a href={telHref(p.phone)} className="h-10 px-3 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium inline-flex items-center gap-1.5" data-testid={`crm-prospect-call-${p.socid}`}><Phone className="h-4 w-4" /> Appeler</a>}
                    <Button variant="outline" size="sm" className="h-10" onClick={() => onOpenClient(p.socid, p.name)} data-testid={`crm-prospect-note-${p.socid}`}><StickyNote className="h-4 w-4 mr-1" /> Note / rappel</Button>
                    <Button variant="ghost" size="sm" className="h-10 text-muted-foreground" disabled={busy === p.socid} onClick={() => snooze(p, 30)} data-testid={`crm-prospect-snooze-${p.socid}`}>{busy === p.socid ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4 mr-1" />} {p.snoozed_until ? 'Remettre' : 'Plus tard (30 j)'}</Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- prospects

function Leads({ onUnauthorized }) {
  const [status, setStatus] = useState('');
  const [mode, setMode] = useState('kanban'); // kanban | liste
  const { data, error, reload, setData } = useLoader(async () => { try { return (await easerGet('/api/easer/crm/leads', { status, days: 180 })).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, [status]);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const update = async (lead, body) => {
    setSaving(true);
    try {
      const res = await easerPut(`/api/easer/crm/leads/${lead.lead_type}/${lead.id}`, body);
      setData((d) => ({ ...d, items: (d.items || []).map((x) => (x.id === lead.id ? res.data.lead : x)) }));
      toast.success(body.status ? `Statut : ${LEAD_STATUS[body.status]?.label || body.status}` : 'Note enregistrée');
      setNoteFor(null);
    } catch (err) { toast.error(errorMessage(err, 'Mise à jour impossible')); } finally { setSaving(false); }
  };

  if (!data) return <Loading />;
  if (error) return <ErrorCard error={error} onRetry={reload} />;
  const items = data.items || [];
  const counts = data.counts || {};
  const statuses = data.statuses || [];
  const leadCard = (l) => (
    <Card className="p-3 space-y-2" data-testid={`crm-lead-${l.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug">{l.name || 'Sans nom'} <span className="text-xs text-muted-foreground font-normal">· {l.type_label}</span></p>
          <p className="text-xs text-muted-foreground">{[l.town && `${l.zip || ''} ${l.town}`.trim(), (l.services || []).join(', ') || l.subject, l.frequency, l.preferred_date && `souhaite le ${l.preferred_date}${l.slot ? ` (${l.slot})` : ''}`].filter(Boolean).join(' · ')}</p>
          <p className="text-xs text-muted-foreground">{fmtDateTime(l.created_at)}</p>
        </div>
        {mode === 'liste' && <StatusBadge map={LEAD_STATUS} value={l.status} testId={`crm-lead-status-${l.id}`} />}
      </div>
      {l.message && <p className="text-sm whitespace-pre-line rounded-lg bg-secondary/60 px-2 py-1.5">{l.message}</p>}
      {l.crm_note && <p className="text-xs rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-2 py-1.5 flex items-start gap-1"><StickyNote className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--primary))]" /> <span>{l.crm_note} <span className="text-muted-foreground">— {l.crm_note_by}</span></span></p>}
      <div className="flex items-center gap-2 flex-wrap">
        {l.phone && <a href={telHref(l.phone)} className="h-10 px-3 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium inline-flex items-center gap-1.5" data-testid={`crm-lead-call-${l.id}`}><Phone className="h-4 w-4" /> Appeler</a>}
        {l.email && <a href={`mailto:${l.email}`} className="h-10 w-10 rounded-lg border border-border inline-flex items-center justify-center" aria-label="E-mail"><Mail className="h-4 w-4" /></a>}
        <Select value={l.status} onValueChange={(v) => update(l, { status: v })}>
          <SelectTrigger className="h-10 w-[132px] text-xs" data-testid={`crm-lead-status-select-${l.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => { setNoteFor(l.id); setNoteText(l.crm_note || ''); }} aria-label="Note" data-testid={`crm-lead-note-${l.id}`}><StickyNote className="h-4 w-4" /></Button>
      </div>
      {noteFor === l.id && (
        <div className="space-y-2">
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Compte rendu d'appel, prochaine étape…" data-testid="crm-lead-note-input" />
          <div className="flex gap-2"><Button size="sm" className="h-10 bg-[hsl(var(--primary))] text-white" disabled={saving} onClick={() => update(l, { crm_note: noteText })} data-testid="crm-lead-note-save">Enregistrer</Button><Button size="sm" variant="ghost" className="h-10" onClick={() => setNoteFor(null)}>Annuler</Button></div>
        </div>
      )}
    </Card>
  );
  const kanbanCols = statuses.filter((s) => s.id !== 'perdu');
  return (
    <div className="space-y-3" data-testid="crm-leads">
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-lg border border-border p-0.5" role="radiogroup" aria-label="Affichage">
          {[['kanban', 'Kanban', KanbanSquare], ['liste', 'Liste', ListIcon]].map(([id, lbl, Icon]) => (
            <button key={id} type="button" role="radio" aria-checked={mode === id} onClick={() => { setMode(id); if (id === 'kanban') setStatus(''); }} data-testid={`crm-leads-mode-${id}`}
              className={`h-9 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1 ${mode === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:bg-secondary'}`}><Icon className="h-3.5 w-3.5" /> {lbl}</button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground ml-auto">{items.length} demande{items.length > 1 ? 's' : ''} · 6 mois</p>
      </div>
      {mode === 'kanban' && (
        <>
          {items.length === 0 && <EmptyLine testId="crm-leads-empty">Aucune demande du site sur les 6 derniers mois.</EmptyLine>}
          {items.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x" data-testid="crm-leads-kanban">
              {kanbanCols.map((s) => {
                const col = items.filter((l) => l.status === s.id);
                return (
                  <div key={s.id} className="w-[82vw] max-w-[320px] flex-shrink-0 snap-start" data-testid={`crm-kanban-col-${s.id}`}>
                    <div className={`rounded-t-xl border px-3 py-2 flex items-center justify-between ${LEAD_STATUS[s.id]?.cls || ''}`}>
                      <span className="text-xs font-semibold uppercase tracking-wide">{s.label}</span><span className="text-xs font-semibold rounded-full bg-white/60 px-2">{col.length}</span>
                    </div>
                    <div className="rounded-b-xl border border-t-0 border-border bg-secondary/30 p-2 space-y-2 min-h-[120px]">
                      {col.length === 0 && <p className="text-xs text-muted-foreground italic text-center py-6">—</p>}
                      {col.map((l) => <div key={l.id}>{leadCard(l)}</div>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {counts.perdu > 0 && <p className="text-[11px] text-muted-foreground">{counts.perdu} demande(s) perdue(s) — visibles dans la vue Liste.</p>}
        </>
      )}
      {mode === 'liste' && (
        <>
          <div className="flex gap-1 overflow-x-auto pb-1">
            <button type="button" onClick={() => setStatus('')} className={`h-9 px-3 rounded-full border text-xs font-semibold whitespace-nowrap ${status === '' ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border text-muted-foreground'}`} data-testid="crm-leads-filter-all">Tous</button>
            {statuses.map((s) => (
              <button key={s.id} type="button" onClick={() => setStatus(status === s.id ? '' : s.id)} className={`h-9 px-3 rounded-full border text-xs font-semibold whitespace-nowrap ${status === s.id ? 'ring-2 ring-offset-1 ring-[hsl(var(--primary))]' : ''} ${LEAD_STATUS[s.id]?.cls || ''}`} data-testid={`crm-leads-filter-${s.id}`}>{s.label}{counts[s.id] ? ` ${counts[s.id]}` : ''}</button>
            ))}
          </div>
          {items.length === 0 && <EmptyLine testId="crm-leads-empty">Aucune demande du site {status ? 'avec ce statut' : 'sur les 6 derniers mois'}.</EmptyLine>}
          <ul className="space-y-2">{items.map((l) => <li key={l.id}>{leadCard(l)}</li>)}</ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- réclamations

function Tickets({ onOpenClient, onUnauthorized }) {
  const [onlyOpen, setOnlyOpen] = useState(true);
  const { data, error, reload } = useLoader(async () => { try { return (await easerGet('/api/easer/crm/tickets')).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, []);
  if (!data) return <Loading />;
  if (error) return <ErrorCard error={error} onRetry={reload} />;
  const items = (data.items || []).filter((t) => !onlyOpen || t.state !== 'resolved');
  return (
    <div className="space-y-3" data-testid="crm-tickets">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{data.open} réclamation(s) ouverte(s) · suivies dans Dolibarr (Tickets)</p>
        <button type="button" onClick={() => setOnlyOpen((v) => !v)} className="text-xs text-[hsl(var(--primary))] font-medium h-9 px-2" data-testid="crm-tickets-toggle">{onlyOpen ? 'Voir les résolues' : 'Ouvertes seulement'}</button>
      </div>
      {items.length === 0 && <EmptyLine testId="crm-tickets-empty">Aucune réclamation {onlyOpen ? 'ouverte' : ''}.</EmptyLine>}
      <ul className="space-y-2">
        {items.map((t) => (
          <li key={t.id}>
            <button type="button" onClick={() => onOpenClient(t.socid, t.client_name)} className="w-full text-left" data-testid={`crm-ticket-${t.id}`}>
              <Card className="p-3">
                <div className="flex items-center justify-between gap-2"><p className="font-medium truncate">{t.client_name}</p><StatusBadge map={TICKET_STATE} value={t.state} fallback={t.status_label} /></div>
                <p className="text-sm">{t.subject}</p>
                <p className="text-xs text-muted-foreground">{t.ref ? `${t.ref} · ` : ''}{t.category_label} · {fmtDateTime(t.created_at)} · {t.status_label}</p>
              </Card>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- incidents terrain

function Incidents({ onUnauthorized }) {
  const { data, error, reload } = useLoader(async () => { try { return (await easerGet('/api/easer/crm/incidents', { days: 30 })).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, []);
  if (!data) return <Loading />;
  if (error) return <ErrorCard error={error} onRetry={reload} />;
  const items = data.items || [];
  return (
    <div className="space-y-2" data-testid="crm-incidents">
      <p className="text-xs text-muted-foreground">Incidents signalés par les Easers sur les 30 derniers jours (retards, clients absents, accès impossible…).</p>
      {items.length === 0 && <EmptyLine testId="crm-incidents-empty">Aucun incident signalé sur la période.</EmptyLine>}
      {items.map((i) => (
        <Card key={i.id} className="p-3" data-testid={`crm-incident-${i.id}`}>
          <div className="flex items-center justify-between gap-2"><p className="font-medium truncate">{i.client_name || i.label || 'Intervention'}</p><Badge className="border text-[10px] bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60"><AlertTriangle className="h-3 w-3 mr-1" /> {i.kind_label}{i.delay_min ? ` · ${i.delay_min} min` : ''}</Badge></div>
          <p className="text-xs text-muted-foreground">{i.easer_name} · {fmtDateTime(i.at)}{i.fichinter_ref ? ` · ${i.fichinter_ref}` : ''}{i.label && i.client_name ? ` · ${i.label}` : ''}</p>
          {i.note && <p className="text-sm mt-1 whitespace-pre-line">{i.note}</p>}
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- équipe

function Team({ onUnauthorized }) {
  const { data, error, reload } = useLoader(async () => { try { return (await easerGet('/api/easer/crm/team')).data; } catch (err) { if (err?.response?.status === 401) onUnauthorized(); throw err; } }, []);
  const colors = useChartColors();
  if (!data) return <Loading />;
  if (error) return <ErrorCard error={error} onRetry={reload} />;
  const items = data.items || [];
  const tot = data.week?.totals || {};
  const chart = items.filter((e) => e.week.planned_minutes || e.week.actual_minutes).map((e) => ({ name: (e.name || '').split(' ')[0], prevu: Math.round((e.week.planned_minutes || 0) / 6) / 10, realise: Math.round((e.week.actual_minutes || 0) / 6) / 10 }));
  return (
    <div className="space-y-3" data-testid="crm-team">
      <Card className="p-3 flex items-center justify-between gap-3">
        <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Semaine du {fmtDay(data.week?.start)}</p><p className="text-sm"><span className="font-semibold">{fmtHours(tot.actual_minutes)}</span> réalisées sur <span className="font-semibold">{fmtHours(tot.planned_minutes)}</span> prévues · {tot.done_count || 0} terminées</p></div>
        <Button variant="ghost" size="icon" className="h-10 w-10" onClick={reload} aria-label="Actualiser" data-testid="crm-team-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </Card>
      {chart.length > 0 && (
        <Card className="p-3" data-testid="crm-team-chart">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Heures de la semaine par Easer</p>
          <div style={{ height: Math.max(120, chart.length * 34) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }} barGap={1} barCategoryGap="28%">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={colors.border} />
                <XAxis type="number" tick={{ fontSize: 10, fill: colors.muted }} axisLine={false} tickLine={false} unit=" h" />
                <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => [`${String(v).replace('.', ',')} h`, n]} contentStyle={{ borderRadius: 10, fontSize: 12 }} cursor={{ fill: colors.border, opacity: 0.4 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                <Bar dataKey="prevu" name="Prévu" fill={colors.muted} opacity={0.45} radius={[0, 3, 3, 0]} />
                <Bar dataKey="realise" name="Réalisé" fill={colors.primary} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
      <ul className="space-y-2">
        {items.map((e) => {
          const pct = e.week.planned_minutes ? Math.min(100, Math.round((e.week.actual_minutes / e.week.planned_minutes) * 100)) : 0;
          return (
            <li key={e.id}>
              <Card className="p-3 space-y-2" data-testid={`crm-team-${e.id}`}>
                <div className="flex items-center gap-2">
                  <span className="h-9 w-9 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] font-semibold flex items-center justify-center flex-shrink-0">{(e.name || '?').slice(0, 1)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{e.name} {e.manager && <span className="text-[10px] uppercase text-muted-foreground font-semibold">manager</span>}</p>
                    <p className="text-xs text-muted-foreground flex flex-wrap gap-1">{(e.tags || []).map((t) => <Badge key={t} className={`border text-[10px] ${tagStyle(t).badge}`}>{t}</Badge>)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]" /> {fmtAvg(e.score?.average)}</p>
                    <p className="text-[10px] text-muted-foreground">{e.score?.count || 0} évaluation{e.score?.count > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtHours(e.week.actual_minutes)} / {fmtHours(e.week.planned_minutes)}</span><span>{e.week.done_count}/{e.week.planned_count} créneaux{e.week.not_clocked_past ? ` · ${e.week.not_clocked_past} non pointé(s)` : ''}</span></div>
                  <div className="h-1.5 rounded-full bg-secondary mt-1 overflow-hidden"><div className="h-full bg-[hsl(var(--primary))] rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> {e.qc_month.count} contrôle{e.qc_month.count > 1 ? 's' : ''}{e.qc_month.average ? ` (${fmtAvg(e.qc_month.average)})` : ''} ce mois</span>
                  {e.acc_month.count > 0 && <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" /> {e.acc_month.km} km accompagnement{e.acc_month.km_to_bill ? ` · ${e.acc_month.km_to_bill} à facturer` : ''}</span>}
                  {e.score?.quality?.count > 0 && <span><StarRating value={e.score.quality.average} size="h-3 w-3" /> qualité ×2</span>}
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
